import { normalizeText } from "@/lib/fuzzy";
import { calcDelta } from "@/lib/utils";
import type { CustomerStat, ProductStat } from "@/services/wholesale-analytics";

/** Pretraga i sortiranje tabela VP analitike (artikli, kupci): čista logika, bez React-a. */

export type SortDir = "asc" | "desc";
export type SortState<K extends string> = { key: K; dir: SortDir };

/** Vrijednost po kojoj se sortira; `null` znači „nema podatka" i uvijek ide na kraj. */
type Getter<T> = (row: T) => number | string | null;

/** Promjena prema prethodnom periodu kao broj za sortiranje; „novo" je iznad svakog rasta. */
function deltaValue(current: number, previous: number): number | null {
  if (previous === 0) return current > 0 ? Number.POSITIVE_INFINITY : null;
  return calcDelta(current, previous);
}

function sortBy<T, K extends string>(
  list: T[], getters: Record<K, Getter<T>>, sort: SortState<K>, tiebreak: (a: T, b: T) => number,
): T[] {
  const get = getters[sort.key];
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    if (va === null || vb === null) {
      if (va !== vb) return va === null ? 1 : -1;
      return tiebreak(a, b);
    }
    const cmp = typeof va === "string" ? va.localeCompare(vb as string, "bs") : va - (vb as number);
    return cmp * sign || tiebreak(a, b);
  });
}

/** Svaka riječ upita mora se naći negdje u tekstu reda (redoslijed nije bitan). */
function matches(haystack: string, query: string): boolean {
  const words = normalizeText(query).split(" ").filter(Boolean);
  if (words.length === 0) return true;
  const text = normalizeText(haystack);
  return words.every((w) => text.includes(w));
}

export type ProductSortKey = "name" | "units" | "delta" | "revenue" | "customers" | "stock" | "str";

const PRODUCT_GETTERS: Record<ProductSortKey, Getter<ProductStat>> = {
  name: (p) => p.name,
  units: (p) => p.units,
  delta: (p) => deltaValue(p.units, p.prevUnits),
  revenue: (p) => p.revenue,
  customers: (p) => p.customers,
  stock: (p) => (p.stock === null ? null : Math.max(0, p.stock)),
  str: (p) => p.str,
};

export function sortProducts(list: ProductStat[], sort: SortState<ProductSortKey>): ProductStat[] {
  return sortBy(list, PRODUCT_GETTERS, sort, (a, b) => b.units - a.units || b.revenue - a.revenue);
}

/** Artikal se nalazi po nazivu, šifri ili nazivu/šifri bilo koje varijacije. */
export function filterProducts(list: ProductStat[], query: string): ProductStat[] {
  if (!query.trim()) return list;
  return list.filter((p) =>
    matches([p.name, p.sku, ...p.variations.flatMap((v) => [v.label, v.sku])].join(" "), query));
}

export type CustomerSortKey = "name" | "revenue" | "delta" | "orders" | "units" | "avgOrder" | "lastOrderAt";

const CUSTOMER_GETTERS: Record<CustomerSortKey, Getter<CustomerStat>> = {
  name: (c) => c.name,
  revenue: (c) => c.revenue,
  delta: (c) => deltaValue(c.revenue, c.prevRevenue),
  orders: (c) => c.orders,
  units: (c) => c.units,
  avgOrder: (c) => c.avgOrder,
  lastOrderAt: (c) => c.lastOrderAt,
};

export function sortCustomers(list: CustomerStat[], sort: SortState<CustomerSortKey>): CustomerStat[] {
  return sortBy(list, CUSTOMER_GETTERS, sort, (a, b) => b.revenue - a.revenue);
}

export function filterCustomers(list: CustomerStat[], query: string): CustomerStat[] {
  if (!query.trim()) return list;
  return list.filter((c) => matches(c.name, query));
}

/** Klik na kolonu: ista kolona okreće smjer, nova kreće od najvećeg (naziv od A). */
export function nextSort<K extends string>(current: SortState<K>, key: K): SortState<K> {
  if (current.key === key) return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  return { key, dir: key === "name" ? "asc" : "desc" };
}
